import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../../components/layout/Container/Container';
import { Row, Col } from '../../components/layout/Grid/Grid';
import Card, { CardBody } from '../../components/ui/Card/Card';
import Badge from '../../components/ui/Badge/Badge';
import Button from '../../components/ui/Button/Button';
import cvData from '../../assets/data/cv-data';
import './Project.css';

const Project = () => {
  const { id } = useParams();
  const [activeImage, setActiveImage] = useState(0);

  // Find project by id
  const project = cvData.projects.find(p => p.id === parseInt(id)) || cvData.projects[0];
  
  // Get related projects (excluding current)
  const relatedProjects = cvData.projects
    .filter(p => p.id !== project.id && p.category === project.category)
    .slice(0, 3);

  // Mock gallery images
  const galleryImages = [
    project.image,
    "https://via.placeholder.com/800x400?text=Screenshot+2",
    "https://via.placeholder.com/800x400?text=Screenshot+3",
    "https://via.placeholder.com/800x400?text=Screenshot+4"
  ];

  return (
    <div className="project-detail-page">
      {/* Hero Section */}
      <section className="bg-primary text-white py-5">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4 fw-bold mb-3">{project.title}</h1>
              <p className="lead mb-4">{project.description}</p>
              <div className="d-flex gap-3 justify-content-center">
                <a href={project.liveUrl} target="_blank" rel="noopener noreferrer"
                   className="btn btn-light btn-lg px-4">
                  <i className="fas fa-external-link-alt me-2"></i>
                  Live Demo
                </a>
                <a href={project.githubUrl} target="_blank" rel="noopener noreferrer"
                   className="btn btn-outline-light btn-lg px-4">
                  <i className="fab fa-github me-2"></i>
                  Source Code
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="py-5">
        <Row>
          {/* Main Content */}
          <Col lg={8}>
            {/* Project Gallery */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <div className="main-image mb-3">
                  <img 
                    src={galleryImages[activeImage]} 
                    alt={project.title}
                    className="img-fluid rounded w-100"
                    style={{ maxHeight: '500px', objectFit: 'cover' }}
                  />
                </div>
                <Row className="g-2">
                  {galleryImages.map((img, index) => (
                    <Col xs={3} key={index}>
                      <img 
                        src={img} 
                        alt={`Gallery ${index + 1}`}
                        className={`img-fluid rounded cursor-pointer ${activeImage === index ? 'border border-3 border-primary' : ''}`}
                        style={{ height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => setActiveImage(index)}
                      />
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>

            {/* Project Details */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h2 className="h4 fw-bold mb-4">Project Overview</h2>
                <p className="text-muted mb-4">
                  This is a comprehensive {project.title} built using modern web technologies. 
                  The project demonstrates best practices in web development and includes features 
                  such as responsive design, user authentication, and real-time updates.
                </p>

                <h3 className="h5 fw-bold mb-3">Key Features</h3>
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Responsive design for all devices
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        User authentication and authorization
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Real-time data updates
                      </li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        RESTful API integration
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Database management
                      </li>
                      <li className="mb-2">
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Performance optimized
                      </li>
                    </ul>
                  </Col>
                </Row>

                <h3 className="h5 fw-bold mb-3">Technologies Used</h3>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {project.technologies.map(tech => (
                    <Badge key={tech} variant="primary" className="px-3 py-2">
                      {tech}
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* Project Challenges */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h2 className="h4 fw-bold mb-4">Challenges & Solutions</h2>
                <div className="challenge-item mb-4">
                  <h3 className="h6 fw-bold mb-2">Challenge 1: Performance Optimization</h3>
                  <p className="text-muted small mb-3">
                    The initial version had slow loading times due to large images and unoptimized code.
                  </p>
                  <h4 className="h6 fw-bold mb-2">Solution:</h4>
                  <p className="text-muted small">
                    Implemented lazy loading for images, code splitting, and optimized bundle size using Webpack.
                  </p>
                </div>

                <div className="challenge-item mb-4">
                  <h3 className="h6 fw-bold mb-2">Challenge 2: Real-time Updates</h3>
                  <p className="text-muted small mb-3">
                    Needed to implement real-time features without overloading the server.
                  </p>
                  <h4 className="h6 fw-bold mb-2">Solution:</h4>
                  <p className="text-muted small">
                    Used WebSocket connections with proper throttling and implemented efficient state management.
                  </p>
                </div>

                <div className="challenge-item">
                  <h3 className="h6 fw-bold mb-2">Challenge 3: Cross-browser Compatibility</h3>
                  <p className="text-muted small mb-3">
                    Some features didn't work consistently across different browsers.
                  </p>
                  <h4 className="h6 fw-bold mb-2">Solution:</h4>
                  <p className="text-muted small">
                    Used polyfills and modern CSS with fallbacks, tested extensively across browsers.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Project Info */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody>
                <h3 className="h5 fw-bold mb-3">Project Info</h3>
                <ul className="list-unstyled">
                  <li className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Category:</span>
                    <span className="fw-semibold">{project.category}</span>
                  </li>
                  <li className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Technologies:</span>
                    <span className="fw-semibold">{project.technologies.length}</span>
                  </li>
                  <li className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Status:</span>
                    <Badge variant="success">Completed</Badge>
                  </li>
                  <li className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Last Update:</span>
                    <span className="fw-semibold">2024</span>
                  </li>
                </ul>

                <hr className="my-3" />

                <h4 className="h6 fw-bold mb-3">Quick Actions</h4>
                <div className="d-grid gap-2">
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" 
                     className="btn btn-primary">
                    <i className="fas fa-external-link-alt me-2"></i>
                    Visit Live Site
                  </a>
                  <a href={project.githubUrl} target="_blank" rel="noopener noreferrer"
                     className="btn btn-outline-secondary">
                    <i className="fab fa-github me-2"></i>
                    View on GitHub
                  </a>
                </div>
              </CardBody>
            </Card>

            {/* Related Projects */}
            {relatedProjects.length > 0 && (
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <h3 className="h5 fw-bold mb-3">Related Projects</h3>
                  {relatedProjects.map(related => (
                    <Link to={`/project/${related.id}`} key={related.id} 
                          className="text-decoration-none">
                      <div className="d-flex align-items-center mb-3 p-2 rounded hover-bg-light">
                        <img 
                          src={related.image} 
                          alt={related.title}
                          className="rounded me-3"
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        />
                        <div>
                          <h4 className="h6 fw-bold mb-1 text-dark">{related.title}</h4>
                          <p className="small text-muted mb-0">{related.technologies.length} technologies</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>

        {/* Navigation */}
        <Row className="mt-4">
          <Col className="d-flex justify-content-between">
            <Link to="/projects" className="btn btn-outline-primary">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Projects
            </Link>
            <div>
              <Button variant="outline-secondary" className="me-2">
                <i className="fas fa-share-alt me-2"></i>
                Share
              </Button>
              <Button variant="outline-secondary">
                <i className="far fa-bookmark"></i>
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Project;